import Modal from '@/components/Modal';
import { hideModal } from '@/utils/modal.utils';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface ImagePreviewModalProps {
  id: string;
  title?: string;
  children: React.ReactNode;
  containerClassName?: string;
  bodyClassName?: string;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onClickOutsideContent?: () => void;
  onEscapePress?: () => void;
}

export const ImagePreviewModal = ({
  id,
  title,
  children,
  containerClassName,
  bodyClassName,
  isOpen,
  onOpen,
  onClose,
  onClickOutsideContent,
  onEscapePress,
}: ImagePreviewModalProps) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 }); // Default center
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = normal, 4.5 = max zoom
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // Pan offset
  const [isDragging, setIsDragging] = useState(false); // Drag state
  const [, setDragStart] = useState({ x: 0, y: 0 }); // Initial drag position
  const [lastDragPosition, setLastDragPosition] = useState({ x: 0, y: 0 }); // Last drag position

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        setIsImageZoomed(false);
        setZoomOrigin({ x: 50, y: 50 });
        setZoomLevel(1);
        setDragOffset({ x: 0, y: 0 });
        setIsDragging(false);
        setDragStart({ x: 0, y: 0 });
        setLastDragPosition({ x: 0, y: 0 });
      }, 500);
      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [isOpen]);

  // Prevent page zoom when modal is open
  useEffect(() => {
    const preventPageZoom = (e: WheelEvent) => {
      if (isOpen && e.ctrlKey) {
        e.preventDefault();
      }
    };

    if (isOpen) {
      document.addEventListener('wheel', preventPageZoom, { passive: false });
    }

    return () => {
      document.removeEventListener('wheel', preventPageZoom);
    };
  }, [isOpen]);

  return (
    <Modal
      id={id}
      title={title}
      size="medium"
      showHeader={false}
      showFooter={false}
      containerClassName={twMerge(
        // 'top-5 bg-black max-w-none rounded-none',
        // 'top-5 bg-black w-max max-w-none rounded-none min-[1920px]:top-1/2 min-[1920px]:-translate-y-1/2',
        'top-1/2 -translate-y-1/2 bg-black w-max !max-w-[97vw] rounded-none',
        containerClassName
      )}
      bodyClassName={twMerge(
        // 'relative m-none p-0 h-max max-h-[90vh] flex items-center min-[1920px]:max-h-[1060px]',
        // 'relative m-none p-0 h-[90vh] flex items-center min-[1920px]:h-[1060px]',
        // 'relative m-none p-0 h-[75vh] flex items-center min-[1920px]:h-[1420px]',
        // 'relative m-none p-0 h-[75vh] flex items-center min-[1920px]:h-[1060px]',
        // 'relative m-none p-0 h-max max-h-[75vh] flex items-center min-[1920px]:h-[1060px]',
        'relative m-none p-0 h-max max-h-[99vh] flex items-center',
        bodyClassName
      )}
      customCloseButton={
        <div
          className="absolute top-3 right-5 bg-black/75 px-1.75 py-0.75 rounded-full z-50 hover:cursor-pointer"
          onClick={() => {
            hideModal(`#${id}`);
            onClose?.();
          }}
        >
          <i className="ki-outline ki-cross text-white/95 text-2xl" />
        </div>
      }
      onOpen={onOpen}
      onClickOutsideContent={onClickOutsideContent}
      onEscapePress={onEscapePress}
    >
      <div
        className={`relative h-full w-full flex justify-center items-center after:content-[''] after:absolute after:top-0 after:left-0 after:w-full after:h-full after:cursor-default ${
          isDragging || isImageZoomed ? '' : 'transition-transform duration-300'
        } ${isImageZoomed ? 'after:cursor-grab' : 'after:cursor-default'} ${
          isDragging ? 'after:cursor-grabbing' : ''
        }`}
        style={{
          transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
          transform: `scale(${zoomLevel}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        }}
        onMouseDown={(e) => {
          if (isImageZoomed) {
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
            setLastDragPosition({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseMove={(e) => {
          if (isDragging && isImageZoomed) {
            const deltaX = (e.clientX - lastDragPosition.x) * 0.8;
            const deltaY = (e.clientY - lastDragPosition.y) * 0.8;
            setDragOffset((prev) => ({
              x: prev.x + deltaX,
              y: prev.y + deltaY,
            }));
            setLastDragPosition({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseUp={() => {
          setIsDragging(false);
        }}
        onMouseLeave={() => {
          setIsDragging(false);
        }}
        onDoubleClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          setZoomOrigin({ x, y });
          setIsImageZoomed(!isImageZoomed);
          setZoomLevel(isImageZoomed ? 1 : 4.5);
          // Reset drag offset when zooming out
          if (isImageZoomed) {
            setDragOffset({ x: 0, y: 0 });
          }
        }}
        onWheel={(e) => {
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          setZoomOrigin({ x, y });

          // const delta = e.deltaY > 0 ? -0.03 : 0.03;
          const delta = e.deltaY > 0 ? -0.05 : 0.05;
          const newZoomLevel = Math.max(1, Math.min(4.5, zoomLevel + delta));
          setZoomLevel(newZoomLevel);
          setIsImageZoomed(newZoomLevel !== 1);
          // Reset drag offset when zooming out
          if (newZoomLevel === 1) {
            setDragOffset({ x: 0, y: 0 });
          }
        }}
      >
        {children}
      </div>
    </Modal>
  );
};
